import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ArrowRight, Calendar, Tag } from 'lucide-react';
import SEO from '../components/SEO';
import { fetchArticles, fetchCategories, mediaUrl } from '../lib/blogApi';

const PLACEHOLDER = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_cargo-urgent/artifacts/dsr13j63_airfreight%20pic%202.png';

const Insights = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('All');
  const [loading, setLoading] = useState(true);

  const load = async (s = search, c = activeCat) => {
    setLoading(true);
    try {
      const data = await fetchArticles(s, c === 'All' ? '' : c);
      setArticles(data);
    } catch (e) { setArticles([]); }
    setLoading(false);
  };

  useEffect(() => {
    fetchCategories().then((c) => setCategories(['All', ...c])).catch(() => setCategories(['All']));
    load('', 'All');
    // eslint-disable-next-line
  }, []);

  const fmt = (d) => { try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ''; } };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="ATC Insights | Freight, AOG & Logistics Articles" description="Educational articles and freight-market updates on AOG logistics, Incoterms, customs compliance, dangerous goods and global transportation trends." path="/insights" />
      <section className="bg-[#003057] pt-40 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-[#C9A227] font-semibold tracking-wide uppercase text-sm">Knowledge Hub</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-4">ATC Insights</h1>
          <p className="text-lg text-gray-300 max-w-2xl">Expert articles on time-critical freight, AOG logistics, Incoterms, customs compliance, dangerous goods and the global forces shaping transportation.</p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search + filters */}
          <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between mb-10">
            <div className="relative md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text" value={search} placeholder="Search articles..."
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load(search, activeCat)}
                className="pl-10" data-testid="insights-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => { setActiveCat(cat); load(search, cat); }}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${activeCat === cat ? 'bg-[#0089D0] text-white' : 'bg-[#F7F9FB] text-[#003057] hover:bg-[#E5E9EF]'}`}
                  data-testid={`insights-cat-${cat}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <p className="text-[#4A4A4A] py-16 text-center">Loading articles...</p>
          ) : articles.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#4A4A4A] text-lg">No articles found. Please check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((a, i) => (
                <motion.div key={a.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Link to={`/insights/${a.slug}`} className="block group" data-testid={`insights-card-${i}`}>
                    <div className="bg-white border border-[#E5E9EF] rounded-lg overflow-hidden hover:shadow-xl transition-all h-full">
                      <div className="h-48 overflow-hidden">
                        <img src={mediaUrl(a.featured_image) || PLACEHOLDER} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-3 text-xs text-[#0089D0] font-semibold mb-3">
                          <span className="bg-[#0089D0]/10 px-2 py-1 rounded">{a.category}</span>
                          <span className="flex items-center gap-1 text-gray-400"><Calendar size={12} /> {fmt(a.publication_date)}</span>
                        </div>
                        <h3 className="text-xl font-bold text-[#003057] mb-2 group-hover:text-[#0089D0] transition-colors">{a.title}</h3>
                        <p className="text-[#4A4A4A] text-sm mb-4 line-clamp-3">{a.summary}</p>
                        <span className="text-[#0089D0] font-semibold text-sm flex items-center gap-1">Read Article <ArrowRight size={16} /></span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Insights;
